import React,{ useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Divider, Tooltip } from "@mui/material";
import "./CustomAccordian.css";
import OrderStatusCell from "./OrderStatusCell";
import { getOrderSource } from "../../features/secondaryOrder/ManageSecondaryOrderUtils";
import { ReactComponent as RedirectionIcon } from "../../assets/image/svg/redirectionIcon.svg";
import { useNavigate } from "react-router-dom";
import { GstFlagging } from "../../features/secondaryOrder/ManageSecondaryInvoice";
import { CancelledInvoiceFlagging } from "../../features/secondaryOrder/ManageSecondaryOrder";
import { truncateText } from "../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { ReactComponent as EncricledDisclaimer } from "../../assets/image/svg/encircledDisclaimer.svg";
import InfoDropDown from "../../features/viewOrder/InfoDropDown";
import { truncateToTwoDecimals } from "./DmsUtils";
import {  Select, MenuItem } from "@mui/material";


interface CustomAccordianProps {
  expanded?: boolean;
  statusConfig?: any;
  metaData1: CustomAccordianMetaData[];
  data1: any;
  metaData2?: CustomAccordianMetaData[];
  data2?: any;
  header: string;
  label1: string;
  label2?: string;
  isSecondHalf: boolean;
  dateAndTimeLabel?: string;
  dateAndTimeValue?: any;
  status: string;
  singleColumnInEachHalf?: boolean;
  linkText?: string;
  linkTextOnClick?: () => void;
  onCustomAccordionToggle: (isExpanded: boolean) => void;
  showOrderSource?: boolean;
  redirectionUrl?: string;
  metaData1ChunkSize?: number;
  rejectionReason?: string;
  outletApprovalData?: any;
  isCounterSaleOrder?: boolean;
  metaData2ChunkSize?:number;
  onSiteIdChange?: (siteId: string) => void; 
  onWarehouseIdChange?: (warehouseId: string) => void;
  siteWarehouseMapping?:any;
  metaData1ChunkUnevenSizeArray?:number[]
}

interface CustomAccordianMetaData {
  keys: string;
  label: string;
  index: number;
  valueType?: string;
  currencySymbol?: string;
  render?: string;
  removeEllipsis?: boolean;
  truncateTextAfterChar?: number; 
  valueStyle?: React.CSSProperties;
  focusField?: boolean; 
  dropdownOptions?: (string | { value: string; label: string })[]; 
}



const getNestedValue = (
  data: any,
  keys: string,
  valueType?: string,
  currencySymbol: string = ""
) => {
  return keys.split(".").reduce((acc, key) => {
    if (key.includes("[") && key.includes("]")) {
      const [arrayKey, index] = key.split("[");
      const idx = parseInt(index.replace("]", ""), 10);
      return (
        acc?.[arrayKey]?.[idx] ??
        (valueType?.toLowerCase() == "number" ? 0 : "-")
      );
    }

    const value = acc?.[key];
    if (valueType?.toLowerCase() === "number" && typeof value === "number") {
      return (
        currencySymbol + " " + (value % 1 !== 0 ? truncateToTwoDecimals(value) : value)
      );
    }

    return (
      currencySymbol +
      " " +
      (value ?? (valueType?.toLowerCase() == "number" ? 0 : "-"))
    );
  }, data);
};

const chunkArray = (array: any[], size: number) => {
  const result: any[] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const chunkArrayUnevenly = (array: any[], sizes: number[]) => {
  const result: any[] = [];
  let start = 0;
  for (let s = 0; s < sizes.length; s++) {
    const size = sizes[s];
    result.push(array.slice(start, start + size));
    start += size;
  }
  return result;
};


const CustomAccordian = ({
  expanded,
  onCustomAccordionToggle,
  statusConfig,
  data1,
  metaData1,
  data2,
  metaData2,
  header,
  label1,
  label2,
  isSecondHalf,
  dateAndTimeLabel,
  dateAndTimeValue,
  status,
  linkText,
  linkTextOnClick,
  singleColumnInEachHalf,
  showOrderSource = false,
  redirectionUrl,
  metaData1ChunkSize = 3,
  rejectionReason= "",
  outletApprovalData,
  isCounterSaleOrder,
  metaData2ChunkSize = 3,
  onSiteIdChange,
  onWarehouseIdChange,
  siteWarehouseMapping,
  metaData1ChunkUnevenSizeArray
}: CustomAccordianProps) => {
  const navigate = useNavigate();

  const metaData1Chunks = metaData1ChunkUnevenSizeArray?chunkArrayUnevenly(metaData1,metaData1ChunkUnevenSizeArray):chunkArray(metaData1, metaData1ChunkSize ?? 3);
  const metaData2Chunks = chunkArray(metaData2 || [], metaData2ChunkSize ?? 3);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;
  const [selectedSite, setSelectedSite] = useState("");
  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext ?? "{}").user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);
  
  if(designationEnabled && designation && designationKeys && matchedRole){
    const elementToKeep = matchedRole?.uomAllowed?.case?.trim() ? "Cs" : "Pc";
    metaData1.forEach(element => {
      if (element.keys === "totalQuantities" || element.keys === "totalQuantitiesFOC_QPS"){
        element.label = `Qty (${elementToKeep})`;
      }
    });
  }

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (onCustomAccordionToggle) {
      onCustomAccordionToggle(isExpanded);
    }
  };

  const getFlagging = (rowData: any) => {
    if (rowData?.eInvoiced) return <GstFlagging />;
    else if (rowData?.cancelledInvoice) return <CancelledInvoiceFlagging />;
    return <></>;
  };

  const renderCustomJsx = (meta: CustomAccordianMetaData, tooltip?: boolean) => {
    const ConfigDictionary = JSON.parse(
      localStorage.getItem("ConfigDictionary") ?? "{}"
    );
    const { caseUnitLabel, otherUnitLabel, packUnitLabel } = ConfigDictionary;
    switch (meta?.keys) {
      case "totalQuantities":
        if (tooltip)
          return `${data1?.totalCaseQuantity} ${caseUnitLabel + `, `}${
            otherUnitLabel ? 
            `${data1?.totalOtherUnitQuantity} ${otherUnitLabel + `, `}`
          : ``}${data1?.totalPieceQuantity} ${packUnitLabel}`;
        if(designationEnabled && designation && designationKeys && matchedRole){
          const elementToKeep = matchedRole?.uomAllowed?.case ? "Cs" : "Pc";
          
          return elementToKeep === "Cs" ? `${data1?.totalCaseQuantity} ${caseUnitLabel}` : `${data1?.totalPieceQuantity} ${packUnitLabel}`;
        }
        return (
          <p>
            {data1?.totalCaseQuantity}{" "}
            <span className="CustomAccordian_total_csPc">{caseUnitLabel},</span>{" "}
            {otherUnitLabel && (
              <>
                {data1?.totalOtherUnitQuantity}{" "}
                <span className="CustomAccordian_total_csPc">
                  {otherUnitLabel},
                </span>{" "}
              </>
            )}
            {data1?.totalPieceQuantity}{" "}
            <span className="CustomAccordian_total_csPc">{packUnitLabel}</span>
          </p>
        );
      case "totalQuantitiesFOC_QPS":
        if (tooltip)
          return `${data1?.totalCaseQuantityQpsFoc ?? 0} ${caseUnitLabel + `, `}${
            otherUnitLabel ? 
            `${data1?.totalOtherUnitQuantityQpsFoc ?? 0} ${otherUnitLabel + `, `}`
          : ``}${data1?.totalPieceQuantityQpsFoc ?? 0} ${packUnitLabel}`;
        if(designationEnabled && designation && designationKeys && matchedRole){
          const elementToKeep = matchedRole?.uomAllowed?.case ? "Cs" : "Pc";
          
          return elementToKeep === "Cs" ? `${data1?.totalCaseQuantityQpsFoc ?? 0} ${caseUnitLabel}` : `${data1?.totalPieceQuantityQpsFoc ?? 0} ${packUnitLabel}`;
        }
        return (
          <p>
            {data1?.totalCaseQuantityQpsFoc ?? 0}{" "}
            <span className="CustomAccordian_total_csPc">{caseUnitLabel},</span>{" "}
            {otherUnitLabel && (
              <>
                {data1?.totalOtherUnitQuantityQpsFoc ?? 0}{" "}
                <span className="CustomAccordian_total_csPc">
                  {otherUnitLabel},
                </span>{" "}
              </>
            )}
            {data1?.totalPieceQuantityQpsFoc ?? 0}{" "}
            <span className="CustomAccordian_total_csPc">{packUnitLabel}</span>
          </p>
        );
      default:
        return <></>;
    }
  };

  return (
    <div className="CustomAccordian">
      <Accordion expanded={expanded ?? true} onChange={handleChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#227C9D" }} />}
          className="CustomAccordian_header"
        >
          <div className="CustomAccordian_header">{header}</div>
          <div className="CustomAccordian_status">
            {statusConfig && (
              <OrderStatusCell
                orderStatusConfig={statusConfig}
                statusId={status}
                RenderFlagging={getFlagging(data1)}
                inCustomAccordion={true}
              />
            )}
            {redirectionUrl && (status === "INVOICED" || status === "DELIVERED") && (
              <RedirectionIcon
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(redirectionUrl);
                }}
              />
            )}
            {
              rejectionReason && (
                  <div className="CustomAccordian_rejectionReason">
                    <EncricledDisclaimer style={{ fill: 'rgb(196, 76, 54)', width: '10.5px', height: '10.5px' }} />
                    <span>{rejectionReason}</span>
                  </div>
              )
            }
          </div>
          
          {isCounterSaleOrder && (<InfoDropDown 
            approvalStatus={outletApprovalData?.status?.toUpperCase()}
            approvedDate={outletApprovalData?.lastModifiedTime}
            approvedBy={outletApprovalData?.currentApproverId}
            approvalReason={outletApprovalData?.remarks}
            // approvalStatus={"REJECTED"}
            // approvedDate={"2025-03-25 11:08:06"}
            // approvedBy={"admin"}
            // approvalReason={"Outlet address is Invalid"}
          />)}
      

          {showOrderSource && (
            <div className="CustomAccordian_header_orderStatus">
              Order Source:{" "}
              <span style={{ fontWeight: 500 }}>{getOrderSource(data1)}</span>
            </div>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ padding: "10px 16px 10px" }}>
          <div className="CustomAccordian_details">
            <div className="CustomAccordian_details_left_container" 
            // style={{gap: metaData1ChunkSize >= 5 ? "0px" : "8px"}}
            >
              <div className="CustomAccordian_details_header_container">
                {label1 && (
                  <div className="CustomAccordian_details_header">{label1}</div>
                )}

                {dateAndTimeLabel && (
                  <div className="CustomAccordian_details_header_value">
                    {dateAndTimeLabel}:{" "}
                    <span style={{ fontWeight: 500, color: "#555555" }}>
                      {dateAndTimeValue}
                    </span>
                  </div>
                )}
              </div>

              <div className="CustomAccordian_details_info_container">
                {metaData1Chunks.map((chunk, chunkIndex) => (
                  <div
                    className={`CustomAccordian_details_sub_container ${
                      singleColumnInEachHalf ? "" : "customAccordian_two_row"
                    }`}
                    key={chunkIndex}
                  >
                    {chunk.map((meta: CustomAccordianMetaData) => {
                      return (
                        <div className="flex-item" key={meta.index}>
                          <Tooltip title={meta.label} arrow>
                            <div className="CustomAccordian_label">
                              {meta.label}
                            </div>
                          </Tooltip>
                          <Tooltip
                            title={meta?.render === "Jsx"
                              ? renderCustomJsx(meta, true)
                              : getNestedValue(
                                  data1,
                                  meta.keys,
                                  meta?.valueType,
                                  meta?.currencySymbol
                                )}
                            arrow
                          >
                            <div className="CustomAccordian_value" style={meta.focusField ? { fontSize: "14px" } : undefined}>
                              {meta?.render === "Jsx"
                                ? renderCustomJsx(meta)
                                : getNestedValue(
                                    data1,
                                    meta.keys,
                                    meta?.valueType,
                                    meta?.currencySymbol
                                  )}
                            </div>
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {isSecondHalf && (
              <>
                <Divider
                  style={{
                    padding: "10px 0",
                    borderRight: "2px dotted",
                    color: "#DFE2E7",
                    margin: "0 9px",
                    boxSizing: "border-box",
                  }}
                  orientation="vertical"
                  flexItem
                />
                <div className="CustomAccordian_details_right_container">
                  {label2 && (
                    <div
                      className="CustomAccordian_details_header"
                      style={{ width: "100%" }}
                    >
                      {label2}
                    </div>
                  )}

                  <div style={{ display: "flex", width: "100%", gap: "10px" }}>
                    {metaData2Chunks.map((chunk, chunkIndex) => (
                      <div
                        className={`CustomAccordian_details_sub_container ${
                          singleColumnInEachHalf
                            ? ""
                            : "customAccordian_two_row"
                        }`}
                        key={chunkIndex}
                        style={singleColumnInEachHalf ? {} :{ flex: "0 0 60%" }}
                      >
                        {chunk.map((meta: CustomAccordianMetaData) => {
                          const value = getNestedValue(data2, meta.keys, meta?.valueType, meta?.currencySymbol);
                          const shouldTruncate =
                            meta.truncateTextAfterChar &&
                            typeof value === "string" &&
                            value.length > meta.truncateTextAfterChar; 
                          return (
                            <div className="flex-item" key={meta.index}>
                              <Tooltip title={meta.label} arrow>
                                <div className="CustomAccordian_label">{meta.label}</div>
                              </Tooltip>
                       
                       {(meta?.dropdownOptions) ? (
                       
                       <Select
                            value={value || ""}
                            style={{ width: "60%" }} onChange={(e) => {
                            const newValue = e.target.value;
                            console.log(`Selected ${meta.keys}:`, newValue);
                          
                          if (meta.keys === "siteId" && onSiteIdChange) {
                              const isValidOption = Array.isArray(meta.dropdownOptions) ? meta.dropdownOptions.some(
                                  (opt) => (typeof opt === "string" ? opt : opt.value) === newValue
                              )
                              : false;
                              if (isValidOption) {
                                    onSiteIdChange(newValue);
                                    setSelectedSite(newValue);
    
                              if (onWarehouseIdChange) {
                                  onWarehouseIdChange("");
                                }
                              }
                            } else if (meta.keys === "warehouseId" && onWarehouseIdChange) {
                                const currentSite = selectedSite;
                                const validWarehouses = siteWarehouseMapping[currentSite] || [];
                                const isValidOption = validWarehouses.includes(newValue);
                                if (isValidOption) {
                                  onWarehouseIdChange(newValue);
                                }
                          }
                        }}
                         sx={{ height: "25px", width: "60px", fontSize: "12px" }}
                          displayEmpty
                           renderValue={(selected) => {
                             if (!selected) {
                                console.warn(`No ${meta.keys} selected, returning empty`);
                                return "";
                              }
                            if (Array.isArray(meta.dropdownOptions)) {
                            
                                const option = meta.dropdownOptions.find(
                               (opt) => (typeof opt === "string" ? opt : opt.value) === selected
                              );
                            if (!option) {
                             console.warn(`${meta.keys} "${selected}" not found in dropdownOptions`);
                               return selected; 
                               }
                           return typeof option === "string" ? option : option.label;
                          }
                         return selected; 
                           }}
                          >
                       <MenuItem value="" disabled sx={{ fontSize: "12px", minHeight: "25px", py: 0.5 }}>
                            Select {meta.label}
                        </MenuItem>
                             
                             {meta.keys === "warehouseId" ? 
  
                                (() => {
                                const validWarehouses = selectedSite ? (siteWarehouseMapping[selectedSite] || []) : [];
                                return validWarehouses.map((warehouseId: string) => (
                                <MenuItem
                                    key={warehouseId}
                                    value={warehouseId}
                                    sx={{ fontSize: "12px", minHeight: "25px", py: 0.5 }}
                                    >
                                {warehouseId}
                          </MenuItem>
                     ));
                        })()
                           :
 
                         meta.dropdownOptions?.map((option, index) => (
                            <MenuItem
                                key={typeof option === "string" ? option : option.value}
                                 value={typeof option === "string" ? option : option.value}
                               sx={{ fontSize: "12px", minHeight: "25px", py: 0.5 }}
                             >
                            {typeof option === "string" ? option : option.label}
                         </MenuItem>
                          ))
                        }
                        </Select>
                            ) : (
                            <Tooltip title={getNestedValue(data2, meta.keys)} arrow>
                             <div
                                  className={`CustomAccordian_value ${meta.removeEllipsis ? "no-ellipsis" : ""}`}
                                  style={meta.valueStyle}
                                >
                                   {shouldTruncate && meta.truncateTextAfterChar
                                ? truncateText(getNestedValue(data2, meta.keys), meta.truncateTextAfterChar)
                               : getNestedValue(data2, meta.keys)}
                                </div>
                            </Tooltip>
                            )}                                                        
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  <div
                    className="custom_accordian_link"
                    onClick={linkTextOnClick}
                  >
                    {linkText}
                  </div>
                </div>
              </>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default CustomAccordian;